import Primary from "../layouts/Primary";

import './Award.scss'
import {t} from "../../i18n";
import ScrollingText from "../components/ScrollingText";
import MiniOverlay from "../layouts/MiniOverlay";
import {DisplayAward, DisplayAwardAssignment, DisplayCommand, ShowAward} from "../../javaTypes";
import {DisplayConfig} from "../Config";
import CmpBar from "../layouts/CmpBar";
import ReactConfetti from "react-confetti";
import { useTimer } from "../../hooks";
import { useAsset } from "../../AssetHelper";
import {escape} from "lodash";
import {useContext} from "react";
import DisplayParams from "../DisplayParams";

const awardTypes = {
    judges: 1,
    compass: 2,
    promote: 3,
    control: 4,
    motivate: 5,
    design: 6,
    innovate: 7,
    connect: 8,
    think: 9,
    deansList: 10,
    inspire: 11,
    finalist: 12,
    winner: 13,
    topRanked: 14,
    divisionFinalist: 22,
    divisionWinner: 23,
    conferenceFinalist: 24
}
const smallAllianceRobotTitles = [null, t`Captain`, t`Partner`];
const robotTitles = [null, t`Captain`, t`1<sup>st</sup> Pick`, t`2<sup>nd</sup> Pick`, t`Backup`];
const regularTitles = [null, t`Winner`, t`2<sup>nd</sup> Place`, t`3<sup>rd</sup> Place`];
const rankTitles = [null, t`Ranked 1<sup>st</sup>`, t`Ranked 2<sup>nd</sup>`, t`Ranked 3<sup>rd</sup>`, t`Ranked 4<sup>th</sup>`, t`Ranked 5<sup>th</sup>`, t`Ranked 6<sup>th</sup>`];

function isRobotAwardFunc(a : DisplayAward){
    return a.awardTypeId === awardTypes.finalist || a.awardTypeId === awardTypes.winner || a.awardTypeId === awardTypes.divisionFinalist || a.awardTypeId === awardTypes.divisionWinner || a.awardTypeId === awardTypes.conferenceFinalist;
}

function titleForAssignment({assignment, assignmentCount, isTeamAward, isRobotAward, isDeansList, isTopRanked, noPlace}: {
    assignment: DisplayAwardAssignment,
    assignmentCount: number,
    isTeamAward: boolean,
    isRobotAward: boolean,
    isDeansList: boolean,
    isTopRanked: boolean,
    noPlace?: boolean
}) {
    if(!assignment) {
        return ''
    }
    const titles = isTopRanked ? rankTitles : (isRobotAward ? (assignmentCount < 3 ? smallAllianceRobotTitles : robotTitles) : regularTitles)
    return isDeansList ? "" : (noPlace ? "" : (titles[(assignment.place)] || t`Runner Up`))
}

function AwardAssignment({assignment, assignmentCount, isTeamAward, isRobotAward, isDeansList, isTopRanked, noPlace} : {
    assignment: DisplayAwardAssignment,
    assignmentCount: number,
    isTeamAward: boolean,
    isRobotAward: boolean,
    isDeansList: boolean,
    isTopRanked: boolean,
    noPlace?: boolean
}) {
    if(!assignment) {
        return null
    }

    const winnerString = isTeamAward ?
        assignment.teamNumber :
        (assignment.teamNumber ? `${escape(assignment.name)} (${assignment.teamNumber})` : escape(assignment.name))
    const title = titleForAssignment({assignment, assignmentCount, isTeamAward, isRobotAward, isDeansList, isTopRanked, noPlace})
    return <div className={`awardSeries awardSeries${assignment.place}`}>
        {isDeansList || !isTeamAward ? null : <div className={"awardTeamAvatar"}>
            <div className={`team-avatar team-${assignment.teamNumber}`}></div>
        </div>}
        <div className={"awardTeamInfo"}>
            <div className={"awardTeam"}>
                <span
                  dangerouslySetInnerHTML={{ __html: `${title ? title + ": " : ""} ${winnerString}` }} />
                <span className={"teamName"}>
                    <ScrollingText>
                        {assignment.teamName ? assignment.teamName : ""}
                    </ScrollingText>
                </span>
            </div>
            <span className={"teamLocation"}>
                {/* This would be nice to include but currently is too long */}
                {/*<ScrollingText>*/}
                {/*    {assignment.teamSchool ? assignment.teamSchool : ""}*/}
                {/*</ScrollingText>*/}
                <ScrollingText>
                    {assignment.teamLocation ? assignment.teamLocation : ""}
                </ScrollingText>
            </span>
        </div>
    </div>
}

function DeansListWinners({ totalWinners, assignments }: {
    totalWinners: number,
    assignments: DisplayAwardAssignment[]
}) {
    const crazyLong = totalWinners > 21;

    return <div className={`deansListWinners ${totalWinners > 10 ? 'smallList' : ''}`}>
        {assignments.map (a => <div key={a.place}>{a.teamNumber ? `${a.name} (${a.teamNumber})` : a.name}
            {crazyLong ? null : <><br/><span className={"teamName"}>{a.teamName ? <ScrollingText>{a.teamName}</ScrollingText> : ''}</span></>}
        </div>)}
        {[...Array(totalWinners - assignments.length).keys()].map((_, idx) => <div key={idx}>&nbsp;</div>)}
    </div>;
}

export default function Award({command, config, nowCallback} : {
    command: DisplayCommand<ShowAward>,
    config: DisplayConfig
    nowCallback: () => number
}) {
    const params = useContext(DisplayParams);
    const programLogo = useAsset("img/logos/ftc_horiz_reverse.svg")

    const rows : (DisplayAwardAssignment | null)[] = []
    const isTeamAward = command.params.award.isTeamAward;
    const isRobotAward = isRobotAwardFunc(command.params.award);
    const isDeansList = command.params.award.awardTypeId === awardTypes.deansList;
    const isTopRanked = command.params.award.awardTypeId === awardTypes.topRanked;
    const reverseOrder = isRobotAward || isDeansList;
    const givenCount = command.params.assignments.filter(a => (isTeamAward && a?.teamNumber?.length > 0) || (a?.name?.length > 0)).length;
    if (command.params.place >= 0) {
        const maxPlace = command.params.place + 1;
        // there exists the isTeamAward attribute but it seems to be always false
        command.params.assignments.forEach((assignment) => {
            const shouldShow = reverseOrder ? assignment.place <= maxPlace : assignment.place >= maxPlace;
            if (shouldShow && ((isTeamAward && assignment?.teamNumber?.length > 0) || (assignment?.name?.length > 0))) {
                rows.push(assignment)
            } else {
                rows.push(null)
            }
        })
    }
    const filteredRows: DisplayAwardAssignment[] = rows.filter((r): r is DisplayAwardAssignment => r != null)
    const wantOverlay = config.overlay && config.awardsStyle === 'overlay';
    const isChampions = command.params.award.awardTypeId === awardTypes.inspire && filteredRows.length > 0 && filteredRows[0].place == 1  && filteredRows[0].division == null && params.divisionNumber == 0;
    const { running: confettiRunning } = useTimer({
        startAt: command.ts,
        duration: isChampions ? 60 : 0,
        nowCallback: nowCallback
    })
    if (wantOverlay || config.type === 'cmpBar') {
        const Container = config.type == 'cmpBar' ? CmpBar : MiniOverlay;
        const divisionSuffix = filteredRows.length > 0 && filteredRows[0].division ? t` - ${filteredRows[0].division} Division` : '';
        const assignment = reverseOrder ? filteredRows[filteredRows.length - 1] : filteredRows[0]
        return <Container config={config} header={command.params.award.name + escape(divisionSuffix) + ' ' + titleForAssignment({assignment, assignmentCount: givenCount, isTeamAward, isRobotAward, isDeansList, isTopRanked})} type={'award'} hideMiddleBar collapse={rows.length == 0} className={"awardPage"}>
            {rows.length > 0 ? <AwardAssignment assignment={assignment} assignmentCount={givenCount} isTeamAward={isTeamAward}
                                                isTopRanked={isTopRanked} isRobotAward={isRobotAward} isDeansList={isDeansList} noPlace /> : null}
            {config.type === 'cmpBar' && rows.length == 0 ? <div className={"awardSeries"}><span className={"awardName"} dangerouslySetInnerHTML={{__html: command.params.award.name.replace(/sponsored by.*/,'')}}/></div> : null}
        </Container>

    } else {
        const layoutClass = isTopRanked || isRobotAward ? `assignmentCount${command.params.assignments.filter(a => a.teamNumber).length}` : ''
        const awardName = command.params.award.name.split(" sponsored by ")
        const showSingleResult = !isTopRanked && !isRobotAward && !isDeansList;

        return <Primary>
            <div className={`awardIntro`}>
                {programLogo ? <div className={"programLogoWrapper"}>
                    <img className="programLogo" src={programLogo}/>
                </div> : null }
                <span dangerouslySetInnerHTML={{__html: awardName[0]}} />
                {showSingleResult && rows.length > 0 ? <span className={"awardPlace"} dangerouslySetInnerHTML={{__html: '&nbsp;' + regularTitles[filteredRows[0].place]}} /> : null}
                {filteredRows.length > 0 && filteredRows[0].division ? <div className={"awardDivision"}>&nbsp;{t`- ${filteredRows[0].division} Division`}</div> : null }
                {awardName[1] ? <div className={"awardSponsor"}>&nbsp;{t`sponsored by `}{awardName[1]}</div> : ''}
            </div>

            {showSingleResult ?
                  <AwardAssignment assignmentCount={givenCount}
                                   assignment={reverseOrder ? filteredRows[filteredRows.length - 1] : filteredRows[0]}
                                   isTeamAward={isTeamAward}
                                   isTopRanked={isTopRanked} isRobotAward={isRobotAward}
                                   isDeansList={isDeansList} noPlace />
            :
                isDeansList ? <div className={`traditionalAwardContainer ${layoutClass}`}>
                        <DeansListWinners
                      totalWinners={command.params.assignments.length}
                      assignments={filteredRows} />
                    </div> : rows.map((assignment, idx) => assignment ?
                        <AwardAssignment key={idx} assignment={assignment} assignmentCount={givenCount} isTeamAward={isTeamAward}
                                   isTopRanked={isTopRanked} isRobotAward={isRobotAward} isDeansList={isDeansList} /> :
                  <div key={idx} />)}
            {isChampions ? <ReactConfetti width={1920} height={1080} style={{zIndex: 50}} recycle={confettiRunning} numberOfPieces={1000} tweenDuration={10000} /> : null}
        </Primary>;
    }
}